import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Phone, Globe, MapPin, CheckCircle, AlertTriangle, HelpCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { getAvatarColor, formatPhoneNumber } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export default function ContactTable({ contacts, isLoading, onViewContact, visibleColumns = {}, selectedContactIds, onToggleSelect, onSelectAll }) {
  const { t } = useTranslation();
  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "N/A";
      return format(date, "MMM d, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  const getContactTypeBadge = (contactType) => {
    switch (contactType) {
      case 'active_customer':
        return <Badge className="bg-emerald-100 text-emerald-700 border-0">{t('contactTypes.activeCustomer')}</Badge>;
      case 'past_customer':
        return <Badge className="bg-slate-100 text-slate-600 border-0">{t('contactTypes.pastCustomer')}</Badge>;
      case 'prospect':
      default:
        return <Badge className="bg-blue-100 text-blue-700 border-0">{t('contactTypes.prospect')}</Badge>;
    }
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-0 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            {t('verificationStatus.verified')}
          </Badge>
        );
      case 'invalid':
        return (
          <Badge className="bg-red-100 text-red-700 border-0 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {t('verificationStatus.invalid')}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-100 text-slate-500 border-0 flex items-center gap-1">
            <HelpCircle className="w-3 h-3" />
            {t('verificationStatus.unverified')}
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="space-y-2 p-4">
          {Array(8).fill(0).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
            <tr>
              {onToggleSelect && (
                <th className="px-3 py-3 w-[40px]">
                  <Checkbox
                    checked={contacts.length > 0 && selectedContactIds?.length === contacts.length}
                    onCheckedChange={onSelectAll}
                    className={selectedContactIds?.length > 0 && selectedContactIds.length < contacts.length ? "data-[state=unchecked]:bg-primary/30" : ""}
                  />
                </th>
              )}
              {visibleColumns.name && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[250px]">
                  {t('contacts.tableHeaders.name')}
                </th>
              )}
              {visibleColumns.contactInfo && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[250px]">
                  {t('contacts.tableHeaders.contactInfo')}
                </th>
              )}
              {visibleColumns.location && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[200px]">
                  {t('contacts.tableHeaders.location')}
                </th>
              )}
              {visibleColumns.contactType && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[150px]">
                  {t('contacts.tableHeaders.contactType')}
                </th>
              )}
              {visibleColumns.verificationStatus && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[150px]">
                  {t('contacts.tableHeaders.verification')}
                </th>
              )}
              {visibleColumns.created && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[120px]">
                  {t('contacts.tableHeaders.created')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-800">
            {contacts.map((contact) => {
              const fullName = contact.first_name && contact.last_name
                ? `${contact.first_name} ${contact.last_name}`
                : contact.full_name || 'Unknown Contact';

              const isSelected = selectedContactIds?.includes(contact.id);

              return (
                <tr
                  key={contact.id}
                  className={`hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`}
                  onClick={(e) => {
                    if (onToggleSelect) {
                      onToggleSelect(contact.id);
                      return;
                    }
                    if (e.ctrlKey || e.metaKey) {
                      window.open(`/contact-detail?id=${contact.id}`, '_blank');
                    } else {
                      onViewContact(contact.id);
                    }
                  }}
                >
                  {onToggleSelect && (
                    <td className="px-3 py-4">
                      <Checkbox checked={isSelected} />
                    </td>
                  )}
                  {visibleColumns.name && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-gradient-to-br ${getAvatarColor(contact.first_name || contact.full_name)} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <span className="text-white font-semibold text-sm">
                            {contact.first_name?.charAt(0).toUpperCase() || contact.full_name?.charAt(0).toUpperCase() || 'C'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{fullName}</p>
                        </div>
                      </div>
                    </td>
                  )}
                  {visibleColumns.contactInfo && (
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {contact.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{contact.email}</span>
                          </div>
                        )}
                        {contact.primary_phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{formatPhoneNumber(contact.primary_phone, contact.primary_phone_country_code)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {visibleColumns.location && (
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {contact.country_of_birth && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{contact.country_of_birth}</span>
                          </div>
                        )}
                        {contact.primary_address?.city && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{contact.primary_address.city}, {contact.primary_address.state}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {visibleColumns.contactType && (
                    <td className="px-6 py-4">
                      {getContactTypeBadge(contact.contact_type)}
                    </td>
                  )}
                  {visibleColumns.verificationStatus && (
                    <td className="px-6 py-4">
                      {getVerificationBadge(contact.verification_status)}
                    </td>
                  )}
                  {visibleColumns.created && (
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                      {formatDate(contact.created_at || contact.created_date || contact.createdAt)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
